import { type ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react'
import ReactDatePicker, { type DatePickerProps } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import './index.css'

import { getLocale } from '#/global/component/Form/DatePicker/locale'
import { InputGroup } from '#/global/component/Form/InputGroup'
import type { InputState } from '#/global/component/Form/types'

interface Props {
    children?: ReactNode
    disabled?: boolean
    gap?: string
    required?: boolean
    state?: InputState
    timeIntervals?: number
    timeLabel?: ReactNode
    withTime?: boolean
}

interface SingleProps extends Props {
    onChange: (date: Date | null) => void
    range?: undefined
    rangeValue?: undefined
    value?: Date
}

const locale = getLocale()

export const DatePicker = ({
    onChange,
    timeIntervals,
    value: _value,
    withTime,
    ...inputProps
}: SingleProps) => {
    const { disabled, required } = inputProps
    const [value, setValue] = useState<Date | null | undefined>(_value)

    useEffect(() => {
        setValue(_value)
    }, [_value])

    const onCalendarClose = useCallback(() => {
        value && onChange(value)
    }, [onChange, value])

    const handleChange = useCallback((date: Date | null) => setValue(date), [])

    const withTimeSettings = useMemo(() => ({
        showTimeSelect: true,
        timeFormat: 'HH:mm',
        timeIntervals: timeIntervals ?? 15,
        timeCaption: 'Čas',
    }), [timeIntervals])

    return (
        <ReactDatePicker
            autoComplete="off"
            calendarStartDay={1}
            customInput={<CustomInput withTime={withTime} {...inputProps} />}
            dateFormat={`dd.MM.yyyy${withTime ? ' HH:mm' : ''}`}
            disabled={disabled}
            locale={locale as unknown as DatePickerProps['locale']}
            onCalendarClose={onCalendarClose}
            onChange={handleChange}
            required={required}
            selected={value}
            showPopperArrow={false}
            {...withTime ? withTimeSettings : {}}
        />
    )
}

interface CustomInputProps {
    children?: ReactNode
    className?: string
    gap?: string
    ref?: React.RefObject<HTMLInputElement | null>
    required?: boolean
    state?: InputState
    timeLabel?: ReactNode
    value?: string
    withTime?: boolean
}

const CustomInput = ({ children, className, gap, ref, required, state, timeLabel, value, withTime, ...props }: CustomInputProps) => {
    const dateId = useId()
    const timeId = useId()

    const classNames = [className]
    state && classNames.push(state.level)

    if (withTime) {
        const [dateValue, timeValue] = value?.split(' ') ?? ['', '']
        return (
            <>
                <InputGroup style={{ columnGap: gap }}>
                    <div>
                        <label htmlFor={dateId}>
                            {children}
                            {required && <span className="dek-form-required-indicator">*</span>}
                        </label>
                        <input
                            className={[...classNames, 'dek-form-width--small'].join(' ').trim()}
                            ref={ref}
                            role="textbox"
                            value={dateValue}
                            {...props}
                            id={dateId}
                        />
                    </div>
                    <div>
                        <label htmlFor={timeId}>
                            {timeLabel ?? 'Čas'}
                            {required && <span className="dek-form-required-indicator">*</span>}
                        </label>
                        <input
                            className={[...classNames, 'dek-form-width--xsmall'].join(' ').trim()}
                            ref={ref}
                            role="textbox"
                            value={timeValue}
                            {...props}
                            id={timeId}
                        />
                    </div>
                </InputGroup>
                {state && state.message && <div className={`input-${state.level}`}>{state.message}</div>}
            </>
        )
    }

    return (
        <>
            <label htmlFor={dateId}>
                {children}
                {required && <span className="dek-form-required-indicator">*</span>}
            </label>
            <input
                className={[...classNames, 'dek-form-width--small'].join(' ').trim()}
                ref={ref}
                role="textbox"
                value={value}
                {...props}
                id={dateId}
            />
            {state && state.message && <div className={`input-${state.level}`}>{state.message}</div>}
        </>
    )
}
